/**
 * Bootstrap Workflows Page with Dark Mode Support
 * Clean workflows management interface using Bootstrap classes
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { useWorkflowPersistence } from '../../hooks/useWorkflowPersistence';
import { WorkflowSession } from '../../services/WorkflowPersistenceService';
import { WorkflowStorageUtils, SavedWorkflow } from '../../utils/workflowStorage';
import { WorkflowTemplateModal, WorkflowTemplate } from '../../components/templates/WorkflowTemplateModal';
import { WorkflowTemplateService } from '../../services/WorkflowTemplateService';
import { WorkflowSettingsModal } from '../../components/workflow/WorkflowSettingsModal';

// No demo workflows - clean start for user account
const mockWorkflows: any[] = [];

const categories = ['All', 'My Workflows'];

function formatRelativeTime(date: Date) {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d ago`;
  }
}

function getStatusConfig(status: string) {
  switch (status) {
    case 'active':
      return {
        badgeClass: 'badge bg-success',
        text: 'Active',
        icon: '✅'
      };
    case 'draft':
      return {
        badgeClass: 'badge bg-warning',
        text: 'Draft',
        icon: '📝'
      };
    case 'paused':
      return {
        badgeClass: 'badge bg-secondary',
        text: 'Paused',
        icon: '⏸️'
      };
    default:
      return {
        badgeClass: 'badge bg-light text-dark',
        text: 'Unknown',
        icon: '❓'
      };
  }
}

export function BootstrapWorkflows() {
  const { effectiveTheme } = useBootstrapTheme();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('lastModified');
  const [workflows, setWorkflows] = useState(mockWorkflows);
  const [savedWorkflows, setSavedWorkflows] = useState<SavedWorkflow[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedWorkflowForSettings, setSelectedWorkflowForSettings] = useState<SavedWorkflow | null>(null);
  
  // Use persistence hook for initialization
  const { 
    isInitialized: persistenceInitialized 
  } = useWorkflowPersistence();

  // Filter workflows
  // Load saved workflows function
  const loadSavedWorkflows = useCallback(() => {
    const saved = WorkflowStorageUtils.getAllSavedWorkflows();
    setSavedWorkflows(saved);
  }, []);

  // Load saved workflows on component mount and when page becomes visible
  useEffect(() => {
    // Initial load
    loadSavedWorkflows();
    
    // Also refresh when page becomes visible (user navigates back from editor)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadSavedWorkflows();
      }
    };
    
    const handleFocus = () => {
      loadSavedWorkflows();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadSavedWorkflows]);

  // Handle settings modal
  const handleOpenSettings = (workflowId: string) => {
    const workflow = savedWorkflows.find(w => w.id === workflowId);
    if (workflow) {
      setSelectedWorkflowForSettings(workflow);
      setShowSettingsModal(true);
    }
  };

  const handleSettingsSaved = (updatedWorkflow: SavedWorkflow) => {
    // Refresh the workflows list to show updated data
    loadSavedWorkflows();
  };

  // Convert saved workflows to display format
  const convertSavedToDisplayFormat = (saved: SavedWorkflow) => ({
    id: saved.id,
    name: saved.name,
    description: saved.description || `User-created workflow with ${saved.metadata.nodeCount} nodes and ${saved.metadata.connectionCount} connections`,
    category: 'My Workflows',
    status: saved.isDirty ? 'draft' : 'active',
    lastModified: new Date(saved.metadata.updatedAt),
    executions: 0,
    successRate: 0,
    avgDuration: '0s',
    tags: ['saved', 'personal'],
    author: 'You',
    isPublic: false,
    featured: false,
    isDemo: false,
    hasInterface: false,
    interfaceComponents: 0
  });

  // Combine mock workflows with saved workflows
  const allWorkflows = [
    ...savedWorkflows.map(convertSavedToDisplayFormat),
    ...workflows
  ];

  // Handle workflow deletion
  const handleDeleteWorkflow = async (workflowId: string) => {
    if (confirm('Are you sure you want to delete this workflow?')) {
      // Check if it's a saved workflow
      const savedWorkflow = savedWorkflows.find(w => w.id === workflowId);
      if (savedWorkflow) {
        // Delete from storage and update state
        const success = WorkflowStorageUtils.deleteWorkflow(workflowId);
        if (success) {
          loadSavedWorkflows(); // Refresh the list
        } else {
          console.error('Failed to delete saved workflow:', workflowId);
        }
      } else {
        // Handle mock workflow deletion (not actually deleted, just for demo)
      }
    }
  };

  // Handle template selection
  const handleTemplateSelect = useCallback((template: WorkflowTemplate) => {
    try {
      // Generate workflow from template
      const templateData = WorkflowTemplateService.generateWorkflowFromTemplate(template);
      
      // Create a unique workflow name
      const timestamp = new Date().toLocaleString();
      const workflowName = `${template.name} - ${timestamp}`;
      
      // Save workflow from template
      const workflowData = WorkflowTemplateService.saveWorkflowFromTemplate(templateData, workflowName);
      
      // Save to storage
      const success = WorkflowStorageUtils.saveWorkflow({
        id: workflowData.id,
        name: workflowData.name,
        description: workflowData.description,
        workflow: {
          nodes: templateData.nodes,
          connections: templateData.connections
        },
        isFunction: false,
        metadata: {
          createdAt: workflowData.createdAt,
          updatedAt: workflowData.lastModified,
          version: '1.0.0',
          template: templateData.metadata
        }
      });

      if (success) {
        // Refresh the workflows list
        loadSavedWorkflows();
        
        // Show success message
        alert(`✅ Workflow "${workflowName}" created successfully from template!`);
        
        // Close modal
        setShowTemplateModal(false);
        
        console.log('✅ Created workflow from template:', {
          templateId: template.id,
          templateName: template.name,
          workflowId: workflowData.id,
          workflowName: workflowName
        });
      } else {
        throw new Error('Failed to save workflow to storage');
      }
    } catch (error) {
      console.error('❌ Failed to create workflow from template:', error);
      alert(`❌ Failed to create workflow from template: ${error.message}`);
    }
  }, [loadSavedWorkflows]);

  const filteredWorkflows = allWorkflows.filter(workflow => {
    const matchesCategory = selectedCategory === 'All' || workflow.category === selectedCategory;
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Sort workflows
  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'executions':
        return b.executions - a.executions;
      case 'successRate':
        return b.successRate - a.successRate;
      default: // lastModified
        return b.lastModified.getTime() - a.lastModified.getTime();
    }
  });

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h1 className="h3 mb-1">Workflows</h1>
              <p className="text-muted mb-0">
                Manage and organize your workflow automation pipelines
              </p>
            </div>
            <div className="d-flex gap-2">
              <button 
                className="btn btn-primary"
                onClick={() => setShowTemplateModal(true)}
              >
                <span className="me-2">🚀</span>
                Create from Template
              </button>
              <Link to="/workflows/new" className="btn btn-outline-primary">
                <span className="me-2">➕</span>
                Blank Workflow
              </Link>
              <div className="dropdown">
                <button 
                  className="btn btn-outline-secondary dropdown-toggle" 
                  type="button" 
                  data-bs-toggle="dropdown"
                >
                  Import
                </button>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="#">📁 From File</a></li>
                  <li><a className="dropdown-item" href="#">🔗 From URL</a></li>
                  <li>
                    <button 
                      className="dropdown-item" 
                      onClick={() => setShowTemplateModal(true)}
                    >
                      🚀 From Template
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                {/* Search */}
                <div className="col-lg-4 col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">🔍</span>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search workflows..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Category Filter */}
                <div className="col-lg-3 col-md-6">
                  <select
                    className="form-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* Sort */}
                <div className="col-lg-3 col-md-6">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="lastModified">Last Modified</option>
                    <option value="name">Name</option>
                    <option value="executions">Most Used</option>
                    <option value="successRate">Success Rate</option>
                  </select>
                </div>

                {/* View Mode */}
                <div className="col-lg-2 col-md-6">
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="viewMode"
                      id="grid-view"
                      checked={viewMode === 'grid'}
                      onChange={() => setViewMode('grid')}
                    />
                    <label className="btn btn-outline-secondary" htmlFor="grid-view">
                      ⊞
                    </label>
                    <input
                      type="radio"
                      className="btn-check"
                      name="viewMode"
                      id="list-view"
                      checked={viewMode === 'list'}
                      onChange={() => setViewMode('list')}
                    />
                    <label className="btn btn-outline-secondary" htmlFor="list-view">
                      ≡
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between">
            <p className="text-muted mb-0">
              Showing {sortedWorkflows.length} of {allWorkflows.length} workflows
              {savedWorkflows.length > 0 && (
                <span className="text-success ms-2">
                  • {savedWorkflows.length} saved
                </span>
              )}
            </p>
            <div className="d-flex gap-2 small">
              <span className="badge bg-success">
                {allWorkflows.filter(w => w.status === 'active').length} Active
              </span>
              <span className="badge bg-warning">
                {allWorkflows.filter(w => w.status === 'draft').length} Drafts
              </span>
              <span className="badge bg-secondary">
                {allWorkflows.filter(w => w.status === 'paused').length} Paused
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Workflows Grid/List */}
      <div className="row">
        <div className="col-12">
          {sortedWorkflows.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3" style={{ fontSize: '4rem' }}>📋</div>
              <h4>No workflows found</h4>
              <p className="text-muted mb-4">
                {searchTerm || selectedCategory !== 'All' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first workflow!'
                }
              </p>
              <Link to="/workflows/new" className="btn btn-primary">
                Create New Workflow
              </Link>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="row g-4">
              {sortedWorkflows.map((workflow) => {
                const statusConfig = getStatusConfig(workflow.status);
                return (
                  <div key={workflow.id} className="col-lg-4 col-md-6">
                    <div className={`card h-100 ${workflow.featured ? 'border-primary shadow-sm' : ''}`}>
                      <div className="card-body">
                        <div className="d-flex align-items-start justify-content-between mb-3">
                          <div className="flex-grow-1">
                            <h5 className="card-title mb-2">
                              <Link 
                                to={`/workflows/${workflow.id}`}
                                className="text-decoration-none"
                              >
                                {workflow.name}
                              </Link>
                            </h5>
                            <div className="d-flex gap-2 align-items-center">
                              <span className={statusConfig.badgeClass}>
                                {statusConfig.icon} {statusConfig.text}
                              </span>
                              {workflow.isDemo && (
                                <span className="badge bg-info">
                                  🎯 DEMO
                                </span>
                              )}
                              {workflow.featured && (
                                <span className="badge bg-warning">
                                  ⭐ FEATURED
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="dropdown">
                            <button 
                              className="btn btn-sm btn-outline-secondary"
                              data-bs-toggle="dropdown"
                            >
                              ⋮
                            </button>
                            <ul className="dropdown-menu">
                              <li><Link className="dropdown-item" to={`/workflows/${workflow.id}`}>✏️ Edit Workflow</Link></li>
                              {workflow.hasInterface ? (
                                <>
                                  <li><Link className="dropdown-item" to={`/interface-builder?workflow=${workflow.id}`}>🎨 Edit Interface</Link></li>
                                  <li><Link className="dropdown-item" to={`/executions/${workflow.id}`}>🚀 Run Application</Link></li>
                                </>
                              ) : (
                                <>
                                  <li><Link className="dropdown-item" to={`/interface-builder?workflow=${workflow.id}&new=true`}>➕ Add Interface</Link></li>
                                  <li><Link className="dropdown-item" to={`/executions/${workflow.id}`}>🚀 Run Workflow</Link></li>
                                </>
                              )}
                              <li><hr className="dropdown-divider" /></li>
                              <li><a className="dropdown-item" href="#">📋 Duplicate</a></li>
                              <li><a 
                                className="dropdown-item" 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleOpenSettings(workflow.id);
                                }}
                              >
                                ⚙️ Settings
                              </a></li>
                              <li><a 
                                className="dropdown-item text-danger" 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDeleteWorkflow(workflow.id);
                                }}
                              >
                                🗑️ Delete
                              </a></li>
                            </ul>
                          </div>
                        </div>

                        <p className="card-text text-muted small mb-3">
                          {workflow.description}
                        </p>

                        <div className="mb-3">
                          <div className="d-flex flex-wrap gap-1 align-items-center">
                            {workflow.tags.map((tag, index) => (
                              <span key={index} className="badge bg-light text-dark small">
                                {tag}
                              </span>
                            ))}
                            {workflow.hasInterface && (
                              <span className="badge bg-success-subtle text-success small">
                                🎨 Interface Ready
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="row g-2 text-center small mb-3">
                          <div className="col-3">
                            <div className="text-primary fw-bold">{workflow.executions}</div>
                            <div className="text-muted">Runs</div>
                          </div>
                          <div className="col-3">
                            <div className="text-success fw-bold">{workflow.successRate}%</div>
                            <div className="text-muted">Success</div>
                          </div>
                          <div className="col-3">
                            <div className="text-info fw-bold">{workflow.avgDuration}</div>
                            <div className="text-muted">Avg Time</div>
                          </div>
                          <div className="col-3">
                            <div className={`fw-bold ${workflow.hasInterface ? 'text-success' : 'text-muted'}`}>
                              {workflow.hasInterface ? workflow.interfaceComponents : '—'}
                            </div>
                            <div className="text-muted">Interface</div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between small text-muted mb-3">
                          <span>by {workflow.author}</span>
                          <span>{formatRelativeTime(workflow.lastModified)}</span>
                        </div>

                        {/* Interface Actions */}
                        <div className="d-grid gap-2">
                          {workflow.hasInterface ? (
                            <div className="btn-group" role="group">
                              <Link 
                                to={`/interface-builder?workflow=${workflow.id}`}
                                className="btn btn-outline-primary btn-sm"
                              >
                                🎨 Edit Interface
                              </Link>
                              <Link 
                                to={`/executions/${workflow.id}`}
                                className="btn btn-primary btn-sm"
                              >
                                🚀 Run Application
                              </Link>
                            </div>
                          ) : (
                            <div className="btn-group" role="group">
                              <Link 
                                to={`/interface-builder?workflow=${workflow.id}&new=true`}
                                className="btn btn-outline-primary btn-sm"
                              >
                                ➕ Add Interface
                              </Link>
                              <Link 
                                to={`/executions/${workflow.id}`}
                                className="btn btn-primary btn-sm"
                              >
                                🚀 Run Workflow
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Executions</th>
                        <th>Success Rate</th>
                        <th>Last Modified</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWorkflows.map((workflow) => {
                        const statusConfig = getStatusConfig(workflow.status);
                        return (
                          <tr key={workflow.id}>
                            <td>
                              <div>
                                <Link 
                                  to={`/workflows/${workflow.id}`}
                                  className="fw-medium text-decoration-none"
                                >
                                  {workflow.name}
                                </Link>
                                <div className="small text-muted">
                                  by {workflow.author}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {workflow.category}
                              </span>
                            </td>
                            <td>
                              <span className={statusConfig.badgeClass}>
                                {statusConfig.icon} {statusConfig.text}
                              </span>
                            </td>
                            <td>{workflow.executions}</td>
                            <td>
                              <span className="text-success">
                                {workflow.successRate}%
                              </span>
                            </td>
                            <td>{formatRelativeTime(workflow.lastModified)}</td>
                            <td>
                              <div className="btn-group btn-group-sm" role="group">
                                <Link
                                  to={`/workflows/${workflow.id}`}
                                  className="btn btn-outline-primary"
                                >
                                  Edit
                                </Link>
                                <Link
                                  to={`/executions/${workflow.id}`}
                                  className="btn btn-primary"
                                >
                                  Run
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template Selection Modal */}
      <WorkflowTemplateModal 
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelectTemplate={handleTemplateSelect}
      />

      {/* Workflow Settings Modal */}
      <WorkflowSettingsModal 
        isOpen={showSettingsModal}
        workflow={selectedWorkflowForSettings}
        onClose={() => {
          setShowSettingsModal(false);
          setSelectedWorkflowForSettings(null);
        }}
        onSave={handleSettingsSaved}
      />
    </div>
  );
}